import { APP_ROUTES_PATIENT, APP_ROUTES_RECP_ADD_INTAKE, genderOptions } from '@data'
import { usePatient, useUserRole } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import { addPatientSchema } from '@schema'
import { useAppSelector } from '@store/hooks'
import { AddPatientFormData } from '@type/patient.types'
import {
  Button,
  CheckBox,
  DateInputField,
  DeleteModal,
  FormButton,
  FormInputField,
  InternationalPhoneField,
  Modal,
  SelectInputField
} from '@ui'
import {
  getDuplicatePhoneDetail,
  getError,
  isDuplicatePhoneError,
  isValidInternationalPhone,
  replaceRouteParams,
  showToast,
  toInternationalPhone
} from '@util'
import { Box, Text } from 'grommet'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useProductModules } from '../../../../modules/useProductModules'
import { NO_EMAIL_LABEL, registrationEmailError } from '../../../../utils/patientEmail'

type Props = {
  onClose: () => void
}
export const optionRelationship = ['Mother', 'Father', 'Spouse', 'Brother', 'Sister', 'Son', 'Daughter', 'Grandfather']

export const AddPatient = ({ onClose }: Props) => {
  const { isAdmin, isSuperAdmin } = useUserRole()
  const { isModuleEnabled } = useProductModules()
  const portalEnabled = isModuleEnabled('patient_portal')
  const { user } = useAppSelector(state => state.user)
  const permissions = user?.permissions
  const canStartIntake =
    isSuperAdmin || (!!permissions?.intake?.includes('create') && !!permissions?.intake?.includes('manage'))
  const { openModal, closeModal } = useModalStore()
  const { createPatient } = usePatient()
  const navigate = useNavigate()
  const methods = useForm<AddPatientFormData>({
    defaultValues: {
      charge_registration_fee: false,
      date_of_birth: new Date('01/01/2000').toISOString()
    },
    resolver: yupResolver(addPatientSchema)
  })

  const [showForceCreateModal, setShowForceCreateModal] = useState(false)
  const [duplicatePhoneDetail, setDuplicatePhoneDetail] = useState('')
  const [pendingPayload, setPendingPayload] = useState<Record<string, unknown> | null>(null)
  const [forceCreating, setForceCreating] = useState(false)

  const {
    clearErrors,
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { isSubmitting }
  } = methods
  const noEmail = !!watch('no_email')

  const showSuccess = (patientId?: string) => {
    showToast('Patient created successfully')
    onClose()
    openModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL, {
      children: (
        <div className="flex flex-col gap-4">
          {canStartIntake && (
            <Button
              label="Start Intake"
              onClick={() => {
                navigate(replaceRouteParams(APP_ROUTES_RECP_ADD_INTAKE, { patientId: patientId || '' }))
                closeModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL)
              }}
              size={'wide'}
            />
          )}
          <Button
            kinds="secondary"
            label={isAdmin || isSuperAdmin ? 'Back' : 'Return to patient directory'}
            onClick={() => {
              navigate(isAdmin || isSuperAdmin ? APP_ROUTES_PATIENT : '/')
              closeModal(ADMIN_MODAL_TYPE.SUCCESS_MODAL)
            }}
            size={'wide'}
            style={{ backgroundColor: '#F5F5F5' }}
          />
        </div>
      ),
      description: 'Patient details have been saved. View patients chart by clicking the button below',
      title: 'Patient Added Successfully'
    })
  }

  const buildPayload = (formRecObj: AddPatientFormData, forceCreate = false) => {
    const { charge_registration_fee, email, no_email, ref, ...patientAttrs } = formRecObj
    const phoneNumber = toInternationalPhone(patientAttrs.phone_number || '')
    const sendNoEmail = portalEnabled && !!no_email
    const hospitalRef = ref?.trim()

    return {
      data: {
        attributes: {
          ...patientAttrs,
          phone_number: phoneNumber,
          charge_registration_fee: !!charge_registration_fee,
          ...(forceCreate ? { force_create: true } : {}),
          ...(sendNoEmail ? { no_email: true } : {}),
          ...(hospitalRef ? { ref: hospitalRef } : {})
        },
        relationships: {
          role: {
            data: {
              id: 'patient',
              type: 'role'
            }
          },
          ...(portalEnabled && email && !sendNoEmail
            ? {
                user: {
                  data: {
                    id: email,
                    type: 'user'
                  }
                }
              }
            : {})
        },
        type: 'patient'
      }
    }
  }

  const onSubmit = async (formRecObj: AddPatientFormData) => {
    if (portalEnabled) {
      const emailError = registrationEmailError(formRecObj.email, !!formRecObj.no_email)
      if (emailError) {
        setError('email', { message: emailError, type: 'manual' })
        return
      }
    }

    const phoneNumber = toInternationalPhone(formRecObj.phone_number || '')
    if (!isValidInternationalPhone(phoneNumber)) {
      setError('phone_number', { message: 'Enter a valid phone number', type: 'manual' })
      return
    }

    const formInputVal = buildPayload(formRecObj, false)
    try {
      const response = await createPatient(formInputVal)
      showSuccess(response.data?.id)
    } catch (error: unknown) {
      if (isDuplicatePhoneError(error)) {
        setDuplicatePhoneDetail(
          getDuplicatePhoneDetail(error) ||
            'A patient with this phone number already exists. Use force create to proceed if this phone number is shared.'
        )
        setPendingPayload(formInputVal)
        setShowForceCreateModal(true)
        return
      }
      getError(error)
    }
  }

  const handleForceCreate = async () => {
    if (!pendingPayload) return
    setForceCreating(true)
    try {
      const payload = {
        ...pendingPayload,
        data: {
          ...(pendingPayload.data as Record<string, unknown>),
          attributes: {
            ...((pendingPayload.data as { attributes?: Record<string, unknown> })?.attributes || {}),
            force_create: true
          }
        }
      }
      const response = await createPatient(payload)
      setShowForceCreateModal(false)
      setPendingPayload(null)
      showSuccess(response.data?.id)
    } catch (error: unknown) {
      getError(error)
    } finally {
      setForceCreating(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Add Patient">
        <form className="flex flex-col md:w-[750px] w-full overflow-auto flex-1" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col overflow-auto gap-6 p-6 flex-1">
            <div className="grid md:grid-cols-3 grid-cols-1 gap-6">
              <Box pad="0">
                <FormInputField label="First Name" name="first_name" placeholder="" />
              </Box>
              <Box pad="0">
                <FormInputField label="Middle Name" name="middle_name" placeholder="" />
              </Box>
              <Box pad="0">
                <FormInputField label="Last Name" name="last_name" placeholder="" />
              </Box>
            </div>
            <div>
              <SelectInputField
                label="Gender"
                labelKey={'name'}
                name="gender"
                options={genderOptions}
                placeholder="Select"
                valueKey={{ key: 'value', reduce: true }}
              />
            </div>
            {portalEnabled ? (
              <div>
                <FormInputField disabled={noEmail} label="Portal email (invite)" name="email" placeholder="" />
                <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                  Sends a patient portal invitation when provided.
                </Text>
                <Controller
                  control={control}
                  name="no_email"
                  render={({ field: { onChange, value } }) => (
                    <CheckBox
                      checked={!!value}
                      handleChange={checked => {
                        onChange(checked)
                        if (checked) {
                          setValue('email', '')
                          clearErrors('email')
                        }
                      }}
                      label={NO_EMAIL_LABEL}
                    />
                  )}
                />
              </div>
            ) : null}
            <div>
              <DateInputField label="Date Of Birth" name="date_of_birth" />
            </div>
            <div>
              <InternationalPhoneField label="Phone Number" name="phone_number" />
            </div>
            <div>
              <FormInputField label="Hospital number(patient ref)" name="ref" placeholder="" />
              <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                Leave blank and the system generates one.
              </Text>
            </div>
            <Controller
              control={control}
              name="charge_registration_fee"
              render={({ field: { onChange, value } }) => (
                <Box align="center" direction="row" gap="small" justify="between">
                  <Text size="small">Charge registration fee</Text>
                  <CheckBox checked={!!value} handleChange={checked => onChange(checked)} />
                </Box>
              )}
            />
            <Text color="dark-4" size="xsmall">
              Creates an empty invoice so staff can add the registration charge manually in Billing.
            </Text>
          </div>
          <FormButton disabled={isSubmitting} isLoading={isSubmitting} onClose={onClose} submit="Add Patient" />
        </form>
      </Modal>

      {showForceCreateModal ? (
        <DeleteModal
          description={duplicatePhoneDetail}
          handleSubmit={() => void handleForceCreate()}
          isLoading={forceCreating}
          lBtnLabel="Cancel"
          noIcon
          onClose={() => {
            setShowForceCreateModal(false)
            setPendingPayload(null)
          }}
          rBtnLabel="Force create"
          title="Duplicate phone number"
        />
      ) : null}
    </FormProvider>
  )
}
